import BracketSportHeading from "@/components/bracket/BracketSportHeading"
import EmptyMessage from "@/components/common/EmptyMessage"
import type {
  BracketMatchOrderItem,
  BracketMatchPlayerItem,
} from "@/domain/bracket/model/Bracket"
import { groupBySport } from "@/domain/bracket/utils/BracketUtils"

interface BracketOrderTableProps {
  matchOrders: BracketMatchOrderItem[]
  onSelectBracket: (bracketId: string) => void
}

interface BracketSportOrderTableProps extends BracketOrderTableProps {
  sportLabel: string
}

/** 상대가 없어 다음 라운드로 올라가는 자리 */
const BYE_LABEL = "부전승"
/** 앞선 경기의 승자가 올라올 자리 */
const WINNER_SLOT_LABEL = "승자 진출"

/**
 * 경기 순서 목록.
 *
 * 종이 대진표 1페이지의 「경기 순서표」를 그대로 옮긴 표다 —
 * **종목마다 표를 나눠** 경기를 번호순으로 늘어놓고 체급 · 부 · 성별과 홍 · 청 코너를 한 줄에 담는다.
 * 경기 번호가 종목마다 1부터라 한 표에 섞으면 같은 번호가 여러 번 나온다.
 * 시각 · 링 번호는 이 시스템이 다루지 않아 칸이 없다.
 *
 * 체급 칸을 누르면 그 체급 트리로 넘어간다.
 */
export default function BracketOrderTable({
  matchOrders,
  onSelectBracket,
}: Readonly<BracketOrderTableProps>) {
  if (matchOrders.length === 0) {
    return <EmptyMessage message="편성된 경기가 없습니다." />
  }

  const sportGroups = groupBySport(matchOrders)

  return (
    <div className="flex flex-col gap-10">
      {sportGroups.map((sportGroup) => (
        <section
          key={sportGroup.sportLabel}
          aria-label={`${sportGroup.sportLabel} 경기 순서`}
        >
          <BracketSportHeading
            sportLabel={sportGroup.sportLabel}
            description={`${sportGroup.items.length}경기`}
            level="h3"
          />

          <div className="mt-4">
            <BracketSportOrderTable
              sportLabel={sportGroup.sportLabel}
              matchOrders={sportGroup.items}
              onSelectBracket={onSelectBracket}
            />
          </div>
        </section>
      ))}
    </div>
  )
}

/** 종목 하나의 경기 순서표 */
function BracketSportOrderTable({
  sportLabel,
  matchOrders,
  onSelectBracket,
}: Readonly<BracketSportOrderTableProps>) {
  return (
    <div className="overflow-x-auto">
      <table className="app-table min-w-[720px]">
        <caption className="sr-only">{sportLabel} 경기 순서</caption>

        <thead>
          <tr>
            <th
              scope="col"
              className="w-20 text-center"
            >
              경기
            </th>
            <th scope="col">체급 · 부 · 성별</th>
            <th
              scope="col"
              className="w-24 text-center"
            >
              단계
            </th>
            <th scope="col">홍코너</th>
            <th scope="col">청코너</th>
          </tr>
        </thead>

        <tbody>
          {matchOrders.map((matchOrder) => (
            <tr key={matchOrder.matchId}>
              <td className="text-center font-bold">{matchOrder.orderNo}</td>
              <td>
                <button
                  type="button"
                  onClick={() => onSelectBracket(matchOrder.bracketId)}
                  className="focus-ring app-txt-body2 text-left font-bold text-app-primary-600 underline"
                >
                  {matchOrder.weightClassName}
                </button>
                <span className="app-txt-caption block text-app-text-200">
                  {matchOrder.divisionLabel} · {matchOrder.genderLabel}
                </span>
              </td>
              <td className="text-center">{matchOrder.roundName}</td>
              <td>
                <BracketOrderCorner player={matchOrder.homePlayer} />
              </td>
              <td>
                {matchOrder.isBye ? (
                  <span className="app-txt-body2 text-app-text-200">
                    {BYE_LABEL}
                  </span>
                ) : (
                  <BracketOrderCorner player={matchOrder.awayPlayer} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function BracketOrderCorner({
  player,
}: Readonly<{ player: BracketMatchPlayerItem }>) {
  if (!player.name) {
    return (
      <span className="app-txt-body2 text-app-text-200">
        {WINNER_SLOT_LABEL}
      </span>
    )
  }

  return (
    <span className="app-txt-body2 font-bold">
      {player.name}
      <span className="app-txt-caption block font-medium text-app-text-200">
        {player.gymName}
      </span>
    </span>
  )
}
